////////////////////////////////////////
// # GOLDEN RULE!!!! HONOR ABOVE ALL!!!!
////////////////////////////////////////
// NO NEW FILES!!!!!!!!!
////////////////////////////////////////

/**
 * ════════════════════════════════════════════════════════════════════════════════
 * SIGN-IN PEA - PRESENTATION COMPONENT (POD/PEA PATTERN)
 * ════════════════════════════════════════════════════════════════════════════════
 *
 * ARCHITECTURAL ROLE: Pure presentation component handling UI rendering and user interactions
 *
 * POD/PEA PATTERN:
 *   - POD (PARTNER FILE): Business logic, API calls, state management
 *   - PEA (THIS FILE): Pure UI, event handlers, visual feedback
 *
 * I[R{DVT}]O PATTERN:
 *   INPUT: Props from Pod (formData, callbacks, loading state)
 *   REQUIREMENTS: Accessible UI, validation feedback, loading states
 *   OUTPUT: Rendered form with user interaction callbacks
 *
 * ACCESSIBILITY: WCAG 2.1 AA compliant with ARIA labels and keyboard navigation
 */

import React, { useState } from 'react';
import styles from './Bitsbound_Kings_Customer_Auth_SRC_SignIn_Lobby_Pea.module.css';

/**
 * DEFINITION (D): SignInFormData structure (matches Pod interface)
 */
interface SignInFormData {
  email: string;        // DEFINITION: User email address
  password: string;     // DEFINITION: User password
  rememberMe: boolean;  // DEFINITION: Session persistence flag
  twoFactorCode: string; // DEFINITION: 6-digit TOTP code for 2FA verification
}

/**
 * DEFINITION (D): SignInPeaProps interface defining all props from Pod
 * Enables type-safe prop passing and ensures contract between Pod and Pea
 */
interface SignInPeaProps {
  formData: SignInFormData;                                            // INPUT: Current form state from Pod
  isLoading: boolean;                                                  // INPUT: API call loading state
  error?: string;                                                      // INPUT: Form-level error message
  successMessage?: string | null;                                      // T_VERIFIED_2026_01_02: Success message for email verification
  onFieldChange: (field: keyof SignInFormData, value: string | boolean) => void;  // CALLBACK: Field change handler
  onSubmit: (e: React.FormEvent) => void;                             // CALLBACK: Form submission handler
  onForgotPassword: () => void;                                        // CALLBACK: Forgot password navigation
  onRegisterClick: () => void;                                         // CALLBACK: Register navigation
  onGoogleSignIn?: () => void;                                         // CALLBACK: Google OAuth (optional)
  onGitHubSignIn?: () => void;                                         // CALLBACK: GitHub OAuth (optional)
  onMicrosoftSignIn?: () => void;                                      // CALLBACK: Microsoft OAuth (optional)
  requiresTwoFactor?: boolean;                                         // T_2FA: Whether 2FA code entry is required
  onBack2FA?: () => void;                                              // T_2FA: Callback to go back from 2FA step
}

/**
 * TRANSFORMATION (T): Sign-In Pea component renders sign-in form UI
 * Pure presentation with no business logic, delegating all actions to Pod callbacks
 *
 * INPUT: Props from Pod orchestrator
 * OUTPUT: Rendered sign-in form with accessibility features
 */
const Pea_Lobby_SignIn_SRC_Auth_Customer_Kings: React.FC<SignInPeaProps> = ({
  formData,            // INPUT: Current form values
  isLoading,           // INPUT: Loading state for button
  error,               // INPUT: Form-level error
  successMessage,      // T_VERIFIED_2026_01_02: Success message for email verification
  onFieldChange,       // CALLBACK: Field update handler
  onSubmit,            // CALLBACK: Submit handler
  onForgotPassword,    // CALLBACK: Password reset handler
  onRegisterClick,     // CALLBACK: Registration handler
  onGoogleSignIn,      // CALLBACK: Google OAuth (optional)
  onGitHubSignIn,      // CALLBACK: GitHub OAuth (optional)
  onMicrosoftSignIn,   // CALLBACK: Microsoft OAuth (optional)
  requiresTwoFactor,   // T_2FA: Whether 2FA code entry is required
  onBack2FA            // T_2FA: Callback to go back from 2FA step
}) => {
  // DEFINITION: Local state for password visibility toggle
  const [showPassword, setShowPassword] = useState(false);

  // DEFINITION: Local state for real-time email validation feedback
  const [emailError, setEmailError] = useState('');

  // T_2FA_BACKUP: Local state to toggle between TOTP code and backup code entry
  const [useBackupCode, setUseBackupCode] = useState(false);

  /**
   * VALIDATION (V): Client-side email format validation
   * Provides instant feedback to user without server round-trip
   *
   * INPUT: Email string
   * OUTPUT: Boolean indicating validity
   */
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;  // DEFINITION: RFC 5322 simplified pattern
    return emailRegex.test(email);  // OUTPUT: Validation result
  };

  /**
   * TRANSFORMATION (T): Handles email input changes with real-time validation
   * Provides immediate feedback to improve user experience
   *
   * INPUT: Email string value
   * OUTPUT: Callback invocation and error state update
   */
  const handleEmailChange = (value: string) => {
    onFieldChange('email', value);  // TRANSFORMATION: Notify Pod of change

    // VALIDATION: Real-time email format checking
    if (value && !validateEmail(value)) {
      setEmailError('Please enter a valid email address');  // OUTPUT: Show error
    } else {
      setEmailError('');  // OUTPUT: Clear error
    }
  };

  /**
   * TRANSFORMATION (T): Form submission handler with final validation
   * Prevents submission if validation fails or loading in progress
   *
   * INPUT: Form submit event
   * OUTPUT: Callback invocation if valid
   */
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();  // VALIDATION: Prevent default form submission

    // T_2FA: Different validation for 2FA step vs initial login
    if (requiresTwoFactor) {
      // T_2FA_BACKUP: Different validation for TOTP (6 digits) vs backup code (13 chars with hyphen)
      const codeLength = useBackupCode ? 13 : 6;
      if (formData.twoFactorCode && formData.twoFactorCode.length === codeLength && !isLoading) {
        onSubmit(e);  // TRANSFORMATION: Delegate to Pod handler
      }
    } else {
      // VALIDATION: Initial login - need email and password
      if (validateEmail(formData.email) && formData.password && !isLoading) {
        onSubmit(e);  // TRANSFORMATION: Delegate to Pod handler
      }
    }
  };

  /**
   * TRANSFORMATION (T): Handles 2FA code input with auto-submit
   * T_2FA_BACKUP: Allows numeric (TOTP) or alphanumeric (backup code)
   *
   * INPUT: 2FA code string
   * OUTPUT: Field change callback and optional form submit
   */
  const handle2FACodeChange = (value: string) => {
    if (useBackupCode) {
      // T_2FA_BACKUP: Backup codes are 12-char alphanumeric with hyphen (XXXXXX-XXXXXX format)
      // D: Strip non-alphanumeric except hyphen, uppercase, limit to 12 alphanumeric chars
      const alphanumericOnly = value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(0, 12);
      // T: Auto-format with hyphen after 6th character for better readability
      const formattedValue = alphanumericOnly.length > 6
        ? `${alphanumericOnly.slice(0, 6)}-${alphanumericOnly.slice(6)}`
        : alphanumericOnly;
      onFieldChange('twoFactorCode', formattedValue);
    } else {
      // VALIDATION: TOTP - only allow numeric characters, max 6 digits
      const numericValue = value.replace(/\D/g, '').slice(0, 6);
      onFieldChange('twoFactorCode', numericValue);
    }
  };

  return (
    <div className={styles.signInContainer}>
      <div className={styles.signInCard}>
        <div className={styles.logoSection}>
          {/* T_HOMEPAGE_LINK_2026_01_02: Logo links back to homepage */}
          <a href="https://www.bitsbound.com" className={styles.logo} title="Back to BitsBound homepage">
            <svg xmlns="http://www.w3.org/2000/svg" version="1.1" width="48" height="48" viewBox="0 0 916 872">
              <path d="M0 0 C26.29745068 -0.12760035 52.58183841 0.03615052 78.875 0.5 C79.55309219 0.51195782 80.23118439 0.52391563 80.92982483 0.53623581 C89.2866748 0.6842093 97.64339417 0.83882014 106 1 C104.99865791 85.90088167 103.98332029 170.80153897 102.06449127 305.58102417 C102.00989267 309.32015737 101.95549124 313.0592934 101.90114415 316.79843026 C101.61801643 336.27488329 101.32908312 355.7512404 101.03319991 375.22750384 C100.95946374 380.0896906 100.88723374 384.9518982 100.81567383 389.81411743 C100.72041213 396.28296603 100.62156448 402.75175404 100.52035713 409.22051239 C100.48421394 411.56169726 100.44912895 413.90289878 100.41526604 416.24411774 C100.19827284 431.17796588 99.73573428 446.08240794 99 461 C99.66 461 100.32 461 101 461 C101.2693335 460.40848145 101.53866699 459.81696289 101.81616211 459.20751953 C102.99867532 457.00247015 104.22062222 455.48358441 105.94140625 453.6796875 C106.52019531 453.06738281 107.09898437 452.45507812 107.6953125 451.82421875 C108.31148438 451.18097656 108.92765625 450.53773438 109.5625 449.875 C110.84573922 448.51887904 112.12829229 447.16210848 113.41015625 445.8046875 C114.03164551 445.14823242 114.65313477 444.49177734 115.29345703 443.81542969 C117.39893814 441.57560744 119.45559179 439.29561148 121.5 437 C125.82096085 432.16310352 130.24691669 427.42813485 134.68945312 422.703125 C135.24568359 422.11015625 135.80191406 421.5171875 136.375 420.90625 C136.94003738 420.33101593 137.50507477 419.75578186 138.0872345 419.16311646 C140.07665818 417.0454093 140.07665818 417.0454093 141.51457214 414.48019409 C144.09870036 410.64170609 146.5851598 407.6491241 151.21011734 406.40590668 C158.09222483 405.39429204 165.04932671 405.54809462 171.98828125 405.6015625 C174.25597123 405.58973213 176.5236487 405.57523343 178.79130554 405.55819702 C183.53690817 405.53447728 188.28063256 405.54777367 193.02612305 405.58642578 C199.08351982 405.63214122 205.1352941 405.57871001 211.19213581 405.49630165 C215.87419685 405.44493297 220.55493181 405.45036175 225.23717308 405.47208786 C227.46854947 405.47504529 229.70003159 405.45986563 231.93114662 405.4255867 C251.1376453 405.17842521 251.1376453 405.17842521 257.70939159 411.21327782 C260.02598117 413.68364373 262.01166384 416.2551232 264 419 C265.11199499 420.28222834 266.23328758 421.5566596 267.375 422.8125 C267.9009375 423.42996094 268.426875 424.04742188 268.96875 424.68359375 C271.86294857 427.98409327 274.87199787 431.173612 277.875 434.375 C281.78887343 438.54990049 285.68811123 442.73125565 289.5 447 C293.27344174 451.22400194 297.12835466 455.3663213 301 459.5 C310.62359652 469.77699546 310.62359652 469.77699546 314.62484741 474.45019531 C316.9483052 477.07106834 316.9483052 477.07106834 319.77603149 479.20727539 C326.00985773 484.61024381 328.59689215 488.99435706 329.29118919 497.30137634 C329.52826203 502.95548397 329.52104567 508.59223121 329.4609375 514.25 C329.46835925 516.71518102 329.47904483 519.18035416 329.49282837 521.64550781 C329.50899134 526.79700826 329.48565816 531.946402 329.43408203 537.09765625 C329.37140127 543.67587974 329.40751751 550.24916682 329.47337914 556.82720947 C329.51327685 561.91329634 329.50041172 566.99822743 329.47197342 572.08435059 C329.4646759 574.50826616 329.47349815 576.93228933 329.4992485 579.3560791 C329.67225028 600.21350834 329.67225028 600.21350834 324.22826576 606.7098999 C321.96806823 608.99667599 319.50525132 610.98848705 317 613 C315.1181971 614.92555342 313.34818638 616.9201876 311.5625 618.9375 C307.21449707 623.68335038 302.85068257 628.4144366 298.48291016 633.14208984 C293.30633676 638.74711548 288.15207502 644.37246312 283 650 C281.18779908 651.97944081 279.37532688 653.95863244 277.5625 655.9375 C276.88888428 656.67299072 276.88888428 656.67299072 276.20166016 657.42333984 C273.78124488 660.06402754 271.35523781 662.69936041 268.921875 665.328125 C268.14348267 666.16935913 268.14348267 666.16935913 267.34936523 667.02758789 C266.37051348 668.08480998 265.3909366 669.14136148 264.41040039 670.19702148 C263.95141357 670.69274658 263.49242676 671.18847168 263.01953125 671.69921875 C262.3142907 672.41941444 262.3142907 672.41941444 261.59480286 673.15415955 C259.9164056 674.96731129 259.9164056 674.96731129 258.76358032 677.24783325 C256.44381074 680.89437366 254.17059968 683.46522599 249.83570671 684.53322029 C242.92922483 685.44559976 235.95602062 685.28598833 229.00390625 685.23046875 C226.72760461 685.23907852 224.45131124 685.25021096 222.17503357 685.26374817 C217.4109803 685.28150032 212.64844545 685.26630897 207.88452148 685.22827148 C201.79264692 685.18260937 195.70485721 685.22248441 189.6132822 685.28762341 C184.91427308 685.3277468 180.21623054 685.32044487 175.51713371 685.29921913 C173.27214189 685.29507772 171.0270684 685.30594686 168.78222847 685.33238792 C151.27765765 685.5009418 151.27765765 685.5009418 145.69869995 681.82449341 C143.42566918 679.32256144 141.73016375 676.89216366 140 674 C138.82360282 672.66899282 137.62015432 671.36066798 136.375 670.09375 C135.8186084 669.50062012 135.2622168 668.90749023 134.68896484 668.29638672 C134.13160645 667.7035791 133.57424805 667.11077148 133 666.5 C128.37086457 661.55887149 123.79223953 656.58086514 119.28125 651.53125 C116.03179339 647.92568856 112.73087727 644.36821094 109.42333984 640.81591797 C105.52396258 636.61321146 101.78074297 632.32084911 98 628 C97.34 645.49 96.68 662.98 96 681 C79.93419359 681.04010881 79.93419359 681.04010881 72.82617188 681.04882812 C68.06663837 681.0546953 63.3071249 681.06145014 58.54760742 681.07543945 C54.68955082 681.08670178 50.83151581 681.09236312 46.97344398 681.09487724 C45.53704807 681.09665691 44.10065264 681.10011729 42.66426659 681.10573006 C25.43644076 681.17006146 8.21740146 680.53292379 -9 680 C-8.08297994 596.66471942 -7.16121562 513.32952021 -6.00008392 429.9972229 C-5.94849321 426.29385081 -5.89728441 422.59047356 -5.84620088 418.88709444 C-5.47183228 391.75650102 -5.08110063 364.62658451 -4.56322098 337.49827766 C-3.93243854 304.42627431 -3.54379245 271.35212811 -3.15379912 238.27660418 C-2.96550047 222.32621492 -2.77110698 206.37589817 -2.5762682 190.42558765 C-2.55406173 188.6074491 -2.53187229 186.78931034 -2.50969982 184.97117138 C-2.01895968 144.7363691 -1.47376826 104.50238303 -0.90862036 64.26856375 C-0.8748983 61.86775967 -0.84120377 59.4669552 -0.80751419 57.06615067 C-0.54052546 38.04406935 -0.2715893 19.02201624 0 0 Z " fill="#ffffff" transform="translate(119,92)"/>
              <path d="M0 0 C25.94340983 -0.12164729 51.87329464 0.04297591 77.8125 0.5 C78.83092232 0.51793673 78.83092232 0.51793673 79.86991882 0.53623581 C88.24673631 0.68436189 96.62339112 0.840546 105 1 C105.08538997 29.57329519 104.87468419 58.13378837 104.45991158 86.70395184 C104.38068999 92.20338287 104.30480892 97.7028603 104.22843933 103.20233154 C104.08151698 113.74423106 103.93141109 124.28608284 103.78019267 134.82792151 C103.6584338 143.32537409 103.53844226 151.82285021 103.41952515 160.32034302 C102.77303293 206.51108208 102.12428021 252.70173415 101.35546875 298.890625 C101.33123925 300.34902187 101.30701209 301.80741878 101.28278732 303.26581573 C101.16920827 310.09888903 101.05507222 316.93195298 100.9407959 323.76501465 C100.90886894 325.67479347 100.90886894 325.67479347 100.876297 327.62315369 C100.83382282 330.16263422 100.79129164 332.70211379 100.748703 335.24159241 C100.05851594 376.49693535 99.71029406 417.73923871 100 459 C101.60033274 457.41153973 103.19896209 455.82136322 104.796875 454.23046875 C105.68729492 453.34512451 106.57771484 452.45978027 107.49511719 451.54760742 C110.08391047 448.91465831 112.49913625 446.1896939 114.8984375 443.38671875 C117.82120804 440.0673641 120.84969612 436.85079503 123.875 433.625 C133.4108148 424.36782039 133.4108148 424.36782039 141.15805054 413.83026123 C143.5066809 410.15870092 145.80859894 407.54957266 150.17554855 406.45440292 C157.11249847 405.51040893 164.12257328 405.63714851 171.109375 405.6640625 C173.40091463 405.64782458 175.69244041 405.62951153 177.98394775 405.60922241 C182.78243289 405.57794769 187.57942518 405.58115952 192.37792969 405.60693359 C198.50391621 405.63577037 204.62408578 405.56489459 210.74917221 405.46598244 C215.4818143 405.40317684 220.2130755 405.40111921 224.94605255 405.41614914 C227.20260279 405.41476966 229.4592404 405.39320557 231.71538544 405.35042191 C250.20500521 405.04472841 250.20500521 405.04472841 255.93664551 409.78973389 C258.56936369 412.41116219 260.73998042 415.24740305 262.81269073 418.32328415 C264.79321328 421.12017427 267.14511496 423.51343327 269.5 426 C270.40807454 426.98790934 271.31454025 427.97730209 272.21875 428.96875 C275.13784695 432.1532194 278.06832137 435.32711625 281 438.5 C287.13702617 445.14260258 293.24768628 451.80911659 299.35449219 458.47949219 C300.71528572 459.96568642 302.07634875 461.45163338 303.4375 462.9375 C303.88512695 463.42637695 304.33275391 463.91525391 304.79394531 464.41894531 C307.23788097 467.08584873 309.68879927 469.7460046 312.1484375 472.3984375 C312.67638916 472.96828369 313.20434082 473.53812988 313.74829102 474.12524414 C314.75284688 475.20882235 315.75845444 476.29142704 316.76538086 477.37280273 C317.67918518 478.35873702 318.58491326 479.35224395 319.48078918 480.35449696 C320.96697765 482.00649923 320.96697765 482.00649923 322.55877686 483.44748211 C326.82109792 487.69155441 327.71497481 490.98709035 327.85760498 496.9309845 C327.85174377 497.94674057 327.84588257 498.96249664 327.83984375 500.0090332 C327.85678284 501.09614594 327.87372192 502.18325867 327.89117432 503.30331421 C327.93785672 506.88628326 327.93996023 510.46753146 327.9375 514.05078125 C327.95170811 516.55031517 327.96710683 519.04984258 327.98364258 521.54936218 C328.01051936 526.78556786 328.01404033 532.02133306 328.00195312 537.25756836 C327.99015025 542.622942 328.01827829 547.98633944 328.08398438 553.35131836 C328.16241891 559.8437023 328.19156794 566.33439098 328.18240356 572.82723045 C328.18578084 575.29473348 328.20501734 577.76226955 328.24069214 580.22951698 C328.50195619 600.53415259 328.50195619 600.53415259 324.80810547 605.66488647 C322.33234718 608.1554499 319.90603288 610.04988099 317 612 C315.18326105 613.87428056 315.18326105 613.87428056 313.75 615.6875 C310.32208353 619.69215123 306.73035928 623.53089721 303.125 627.375 C299.21115988 631.54993311 295.31188986 635.73125443 291.5 640 C287.7265588 644.22400134 283.87166228 648.36633784 280 652.5 C275.38003292 657.43326815 270.80888296 662.40180642 266.3046875 667.44140625 C263.39487331 670.67179319 260.4499709 673.86880179 257.5 677.0625 C256.70601807 677.92222412 256.70601807 677.92222412 255.89599609 678.79931641 C251.0902585 683.99568754 251.0902585 683.99568754 247.07472229 684.18754578 C245.27720335 684.22120541 243.47956775 684.24891027 241.68188477 684.2722168 C240.70293747 684.28842575 239.72399017 684.3046347 238.71537781 684.32133484 C229.65705005 684.4618687 220.59772645 684.53972197 211.53854465 684.60025311 C205.45108479 684.64118861 199.36523775 684.70144813 193.27856445 684.80932617 C187.39631806 684.9132371 181.51550913 684.9676851 175.63239861 684.98771095 C173.39364648 685.00361968 171.1549455 685.03679456 168.91671944 685.08786583 C150.47226685 685.48841082 150.47226685 685.48841082 145.45407104 681.83462524 C142.93537654 679.35032244 140.9676148 676.92926089 139 674 C137.93293193 672.75668687 136.85283582 671.5241992 135.75 670.3125 C134.93015625 669.35537109 134.93015625 669.35537109 134.09375 668.37890625 C131.21302163 665.10583987 128.23129278 661.93137154 125.25 658.75 C119.98565601 653.12940124 114.74411739 647.49067545 109.5546875 641.80078125 C107.81706828 639.89577669 106.07197391 637.99756949 104.3203125 636.10546875 C103.49273438 635.20441406 102.66515625 634.30335938 101.8125 633.375 C100.70841797 632.18003906 100.70841797 632.18003906 99.58203125 630.9609375 C98 629 98 629 98 627 C97.34 627 96.68 627 96 627 C96 644.82 96 662.64 96 681 C79.64451886 681.04010881 79.64451886 681.04010881 72.421875 681.04882812 C67.5810429 681.05470076 62.74023047 681.06147229 57.89941406 681.07543945 C35.5796187 681.13814727 13.3059618 680.80248817 -9 680 C-9.3026409 625.1790125 -8.52038433 570.37151364 -7.7804445 515.55665487 C-7.60491132 502.54368558 -7.4340557 489.53065591 -7.26323819 476.51762402 C-6.89443238 448.43939004 -6.51788847 420.36126289 -6.13871133 392.2831673 C-5.9969314 381.78143441 -5.85618738 371.27968777 -5.71558356 360.77793908 C-5.4584915 341.58033059 -5.19855716 322.3827624 -4.93498993 303.1852417 C-4.8953741 300.29937895 -4.85586925 297.41351471 -4.81647491 294.52764893 C-4.45536691 268.07984364 -4.07992771 241.63295836 -3.57104874 215.18753242 C-3.10853885 191.10251891 -2.78257333 167.01735654 -2.5 142.9296875 C-2.47918494 141.16354298 -2.45836305 139.39739855 -2.43753433 137.6312542 C-2.24577075 121.34650322 -2.05816348 105.0617054 -1.875 88.77685547 C-1.78328653 80.62531272 -1.68881908 72.47380629 -1.59148407 64.32232869 C-1.54074159 60.06189996 -1.49135866 55.80146622 -1.44555664 51.54098129 C-1.26012628 34.34302824 -1.03318536 17.17310176 0 0 Z " fill="#20b2aa" transform="translate(490,92)"/>
            </svg>
          </a>
          {/* T_2FA: Dynamic title/subtitle based on 2FA state */}
          <h1 className={styles.title}>
            {requiresTwoFactor ? 'Two-Factor Authentication' : 'Welcome Back'}
          </h1>
          <p className={styles.subtitle}>
            {requiresTwoFactor
              ? (useBackupCode
                  ? 'Enter one of your backup codes'
                  : 'Enter the 6-digit code from your authenticator app')
              : 'Sign in to your BitsBound account'
            }
          </p>
        </div>

        {/* T_2FA: Only show OAuth buttons when NOT in 2FA mode */}
        {!requiresTwoFactor && (onGoogleSignIn || onGitHubSignIn || onMicrosoftSignIn) && (
          <>
            <div className={styles.divider}>
              {onGoogleSignIn && (
                <button
                  type="button"
                  onClick={onGoogleSignIn}
                  className={styles.signUpButton}
                  disabled={isLoading}
                  aria-label="Sign in with Google"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                  </svg>
                  Continue with Google
                </button>
              )}
              {onGitHubSignIn && (
                <button
                  type="button"
                  onClick={onGitHubSignIn}
                  className={styles.signUpButton}
                  disabled={isLoading}
                  aria-label="Sign in with GitHub"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                  </svg>
                  Continue with GitHub
                </button>
              )}
              {onMicrosoftSignIn && (
                <button
                  type="button"
                  onClick={onMicrosoftSignIn}
                  className={styles.signUpButton}
                  disabled={isLoading}
                  aria-label="Sign in with Microsoft"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <rect x="2" y="2" width="9" height="9" fill="#F25022"/>
                    <rect x="13" y="2" width="9" height="9" fill="#7FBA00"/>
                    <rect x="2" y="13" width="9" height="9" fill="#00A4EF"/>
                    <rect x="13" y="13" width="9" height="9" fill="#FFB900"/>
                  </svg>
                  Continue with Microsoft
                </button>
              )}
            </div>
            <div className={styles.divider}>
              <span className={styles.dividerText}>or</span>
            </div>
          </>
        )}

        <form onSubmit={handleSubmit} className={styles.signInForm}>
          {/* T_2FA: Conditionally show email/password OR 2FA code entry */}
          {requiresTwoFactor ? (
            // T_2FA: 2FA Code Entry Mode
            <>
              <div className={styles.formGroup}>
                <label htmlFor="twoFactorCode" className={styles.label}>
                  {useBackupCode ? 'Backup Code' : 'Authentication Code'}
                </label>
                <input
                  id="twoFactorCode"
                  type="text"
                  inputMode={useBackupCode ? 'text' : 'numeric'}
                  pattern={useBackupCode ? '[A-Z0-9-]*' : '[0-9]*'}
                  value={formData.twoFactorCode}
                  onChange={(e) => handle2FACodeChange(e.target.value)}
                  className={styles.input}
                  placeholder={useBackupCode ? 'XXXXXX-XXXXXX' : '000000'}
                  maxLength={useBackupCode ? 13 : 6}
                  autoComplete={useBackupCode ? 'off' : 'one-time-code'}
                  autoFocus
                  style={{
                    textAlign: 'center',
                    fontSize: useBackupCode ? '1.25rem' : '1.5rem',
                    letterSpacing: useBackupCode ? '0.15rem' : '0.5rem',
                    fontFamily: 'monospace'
                  }}
                />
                <p style={{
                  color: 'rgba(255,255,255,0.6)',
                  fontSize: '0.85rem',
                  marginTop: '0.5rem',
                  textAlign: 'center'
                }}>
                  {useBackupCode
                    ? 'Enter one of your backup codes in XXXXXX-XXXXXX format (each code can only be used once)'
                    : 'Open your authenticator app (Google Authenticator, Authy, etc.) and enter the 6-digit code'
                  }
                </p>
              </div>

              {/* T_2FA_BACKUP: Toggle between TOTP and backup code */}
              <button
                type="button"
                onClick={() => {
                  setUseBackupCode(!useBackupCode);
                  onFieldChange('twoFactorCode', '');  // Clear code when switching modes
                }}
                className={styles.forgotLink}
                style={{ display: 'block', textAlign: 'center', width: '100%', marginBottom: '0.5rem' }}
              >
                {useBackupCode ? '← Use authenticator app instead' : 'Use a backup code instead'}
              </button>

              {/* T_2FA: Back to password entry option */}
              {onBack2FA && (
                <button
                  type="button"
                  onClick={onBack2FA}
                  className={styles.forgotLink}
                  style={{ display: 'block', textAlign: 'center', width: '100%', marginBottom: '1rem' }}
                >
                  ← Use a different account
                </button>
              )}
            </>
          ) : (
            // Normal Login Mode: Email and Password
            <>
              <div className={styles.formGroup}>
                <label htmlFor="email" className={styles.label}>
                  Email Address
                </label>
                <input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleEmailChange(e.target.value)}
                  className={`${styles.input} ${emailError ? styles.inputError : ''}`}
                  placeholder="Enter your email"
                  required
                  autoComplete="email"
                  autoFocus
                />
                {emailError && (
                  <span className={styles.errorMessage}>{emailError}</span>
                )}
              </div>

              <div className={styles.formGroup}>
                <div className={styles.labelRow}>
                  <label htmlFor="password" className={styles.label}>
                    Password
                  </label>
                  <button
                    type="button"
                    onClick={onForgotPassword}
                    className={styles.forgotLink}
                  >
                    Forgot password?
                  </button>
                </div>
                <div className={styles.passwordWrapper}>
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => onFieldChange('password', e.target.value)}
                    className={styles.input}
                    placeholder="Enter your password"
                    required
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className={styles.passwordToggle}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                        <line x1="1" y1="1" x2="23" y2="23"/>
                      </svg>
                    ) : (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                        <circle cx="12" cy="12" r="3"/>
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              <div className={styles.checkboxLabel}>
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={formData.rememberMe}
                    onChange={(e) => onFieldChange('rememberMe', e.target.checked)}
                    className={styles.checkbox}
                  />
                  <span>Remember me for 30 days</span>
                </label>
              </div>
            </>
          )}

          {/* T_VERIFIED_SUCCESS_2026_01_02: Success message for email verification */}
          {successMessage && (
            <div className={styles.submitSuccess}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                <polyline points="22 4 12 14.01 9 11.01"/>
              </svg>
              <span>{successMessage}</span>
            </div>
          )}

          {error && (
            <div className={styles.submitError}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              <span>{error}</span>
            </div>
          )}

          <button
            type="submit"
            className={styles.submitButton}
            disabled={
              isLoading ||
              (requiresTwoFactor
                ? formData.twoFactorCode.length !== (useBackupCode ? 13 : 6)  // T_2FA_BACKUP: 13-char backup (XXXXXX-XXXXXX) or 6-digit TOTP
                : !formData.email || !formData.password || !!emailError  // Normal: Need email & password
              )
            }
          >
            {isLoading ? (
              <>
                <span className={styles.spinner}></span>
                {requiresTwoFactor ? 'Verifying...' : 'Signing in...'}
              </>
            ) : (
              requiresTwoFactor ? 'Verify Code' : 'Sign In'
            )}
          </button>
        </form>

        <div className={styles.footer}>
          <p className={styles.footerText}>
            Don't have an account?{' '}
            <button
              type="button"
              onClick={onRegisterClick}
              className={styles.link}
            >
              Sign up for free
            </button>
          </p>
        </div>

        <div className={styles.footer}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
            <path d="M7 11V7a5 5 0 0110 0v4"/>
          </svg>
          <span>Secured with 256-bit encryption</span>
        </div>
      </div>
    </div>
  );
};

export default Pea_Lobby_SignIn_SRC_Auth_Customer_Kings;